import { StyleSheet, Font } from "@react-pdf/renderer";
import { colors } from "@rubi/ui";

Font.register({
  family: "Helvetica-Bold",
  src: "",
});

export const applyCommonStyles = (styles: any) => {
  return StyleSheet.create({
    ...styles,
  });
};
export const dlPDFHeaderStyles = StyleSheet.create({
  header_container: {
    border: "5px solid var(--text-primary)",
    padding: 10,
    margin: 16,
    maxHeight: "20%",
  },
  logo_container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    margin: 10,
    height: "80%",
    position: "absolute",
    top: 0,
    left: 0,
    right: "80%",
  },
  police_logo: {
    objectFit: "contain",
    maxHeight: "100%",
    width: "auto",
  },
  operational_area: {
    fontSize: 10,
    textAlign: "center",
  },
  confidentiality_text: {
    fontSize: 10,
    color: colors.text.primary,
    textAlign: "center",
    marginBottom: 4,
  },
  crime_types_container: {
    flexDirection: "column",
    alignItems: "center",
    marginLeft: "auto",
    marginRight: "auto",
    justifyContent: "center",
  },
  crime_type: {
    fontSize: 22,
    textTransform: "uppercase",
    fontFamily: "Helvetica-Bold",
  },
  associated_crimes_container: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 4,
  },
  associated_crimes: {
    fontSize: 10,
    borderRadius: 80,
    border: `0.5px solid ${colors.common.black}`,
    padding: 6,
    textAlign: "center",
  },
  incident_text: {
    fontSize: 10,
    textAlign: "center",
  },
  resolved_stamp_container: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  resolved_stamp: {
    color: colors.error.dark,
    fontSize: 34,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    textAlign: "center",
    border: "3px solid var(--error-dark)",
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 12,
    opacity: 0.85,
    transform: "rotate(-10deg)",
  },
});
export const dlPDFImageStyles = StyleSheet.create({
  page_one_image_container: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    padding: 10,
    gap: 8,
    marginBottom: 20,
  },
  image_card: {
    flexDirection: "column",
    fontSize: 10,
    alignItems: "center",
  },
  image_tags_container: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 4,
    marginTop: 8,
  },
  tag: {
    borderRadius: 80,
    border: `0.5px solid ${colors.common.black}`,
    paddingTop: 2,
    paddingBottom: 3,
    paddingHorizontal: 6,
    width: "auto",
    fontSize: 12,
  },
  tag_description_container: {
    display: "flex",
    flexDirection: "column",
    fontSize: 12,
    alignContent: "center",
    marginBottom: 16,
  },
  tag_description_columns: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  page_one_image: {
    width: "100%",
    height: "auto",
    maxHeight: 260,
    objectFit: "contain",
  },
  image_container_remaining: {
    marginBottom: 12,
    marginHorizontal: 24,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: 8,
  },
  image_card_remaining: {
    width: "31%",
    flexDirection: "column",
    alignItems: "center",
  },
  image_remaining: {
    width: "100%",
    height: 180,
    objectFit: "contain",
  },
  more_images_text: {
    borderTop: `1px solid ${colors.common.black}`,
    fontSize: 10,
    textAlign: "center",
    marginTop: 12,
    marginHorizontal: 24,
    paddingTop: 4,
    marginBottom: 2,
  },
  /** The gallery letter an entity row's "Photos" points at, set apart from the labels beside it. */
  photo_letter: {
    borderRadius: 3,
    backgroundColor: colors.common.black,
    color: colors.common.white,
    fontFamily: "Helvetica-Bold",
    paddingTop: 3,
    paddingBottom: 3,
    paddingHorizontal: 5,
    fontSize: 10,
  },
  video_link: {
    fontSize: 10,
    textDecoration: "underline",
    marginTop: 2,
  },
});

/**
 * The entity rows that replaced the tag grid. Proportions follow `BulletinEntityList`: a fixed
 * identity column and the rest of the line for what the record holds.
 */
export const dlPDFEntityStyles = StyleSheet.create({
  container: {
    flexDirection: "column",
    marginBottom: 16,
    borderTop: "0.75px solid grey",
  },
  row: {
    flexDirection: "row",
    paddingVertical: 8,
  },
  row_divider: {
    borderBottom: "0.5px dotted grey",
  },
  identity: {
    width: 170,
    flexDirection: "column",
    gap: 3,
    paddingRight: 12,
  },
  heading: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  field: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  field_label: {
    // "Jurisdiction" at 8pt uppercase, the longest label, plus a gap before its value
    width: 72,
    flexShrink: 0,
    fontSize: 8,
    color: "grey",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    paddingTop: 1.5,
  },
  field_value: {
    flex: 1,
    fontSize: 10,
  },
  details: {
    flex: 1,
    flexDirection: "column",
    gap: 6,
  },
  stored_fields: {
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: 3,
  },
  stored_field: {
    width: "50%",
    paddingRight: 8,
  },
  description: {
    fontSize: 11,
    lineHeight: 1.35,
  },
});

/** A tag pill: the 12pt line box react-pdf lays out, plus padding and border. */
const TAG_PILL_HEIGHT = 20;

/**
 * Roughly one row of additional attachments - the image plus the tag row under
 * it. The heading reserves this much space so it is not left on its own at the
 * foot of a page.
 */
export const ADDITIONAL_IMAGE_ROW_HEIGHT =
  (dlPDFImageStyles.image_remaining.height as number) +
  (dlPDFImageStyles.image_tags_container.marginTop as number) +
  TAG_PILL_HEIGHT;

export const dlPDFDetailsStyles = StyleSheet.create({
  details_container: {
    flexDirection: "column",
    marginLeft: 24,
    marginRight: 24,
  },
  address: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "left",
    fontFamily: "Helvetica-Bold",
  },
  description: {
    fontSize: 12,
    paddingBottom: 12,
    marginTop: 8,
  },
  contact_label: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "left",
    textTransform: "uppercase",
  },
  contact_text: {
    fontSize: 12,
    textAlign: "center",
    paddingBottom: 4,
  },
  contact_details: {
    flexDirection: "row",
    gap: 8,
    marginLeft: 8,
    fontSize: 12,
  },
  police_service_label: {
    fontSize: 10,
    textAlign: "center",
    fontFamily: "Helvetica-Bold",
    marginTop: 12,
  },
  approved_by_label: {
    fontSize: 10,
    textAlign: "center",
    fontFamily: "Helvetica-Bold",
  },
  issue_date: {
    fontSize: 10,
    textAlign: "center",
  },
});

export const dlPDFResolutionStyles = StyleSheet.create({
  resolutions_container: {
    flexDirection: "column",
    marginTop: 12,
    marginLeft: 24,
    marginRight: 24,
    paddingTop: 8,
    borderTop: `1px solid ${colors.common.black}`,
  },
  resolutions_heading: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  resolution_card: {
    flexDirection: "column",
    border: "0.75px solid grey",
    borderRadius: 4,
    padding: 8,
    marginBottom: 8,
  },
  resolution_header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2,
  },
  resolution_reason: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
  },
  resolution_date: {
    fontSize: 9,
    color: "grey",
  },
  resolution_author: {
    fontSize: 9,
    color: "grey",
    marginBottom: 4,
  },
  resolution_details: {
    fontSize: 11,
  },
  resolution_images: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 6,
  },
  resolution_image: {
    objectFit: "contain",
    height: 120,
    width: "31%",
  },
});

export const dlPDFFooterStyles = StyleSheet.create({
  footer_container: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: "row",
    height: "3%",
    borderTop: "1px solid grey",
    fontSize: 8,
    marginLeft: 16,
    marginRight: 16,
    paddingTop: 4,
    color: "grey",
  },
  continued_hint: {
    position: "absolute",
    bottom: 32,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "grey",
  },
});
